import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  QueryObserverResult,
  RefetchOptions,
  useQuery,
} from '@tanstack/react-query';
import {
  CapabilityType,
  CompanyCapability,
} from '@company/types/company-capability.data';
import ICompanyMemberIndicators from '@th-types/company-member-indicators.type';
import getCompany from '@company/services/company/api';
import useCompanyIndicators, {
  clearCompanyIndicatorsCache,
} from '@company/hooks/useCompanyIndicators';

interface CompanyContextData {
  company: ICompanyContext | undefined;
  capabilities: CompanyCapability[];
  isValidated?: boolean;
}

interface ICompanyContext {
  isLoading: boolean;
  isValidated: boolean;
  id: number;
  name: string | null;
  premium: boolean;
  profileURLName: string;
  profileImageUrl: string;
  rating: number;
  reviewsCount: number;
  capabilities: CompanyCapability[];
  indicators: ICompanyMemberIndicators | undefined;
  fetchCompany: (
    options?: RefetchOptions | undefined
  ) => Promise<QueryObserverResult<any, Error>>; // fix type
  fetchNotifications: (
    options?: RefetchOptions
  ) => Promise<QueryObserverResult<ICompanyMemberIndicators, Error>>; // fix type
  hasCapability(capabilityType: CapabilityType): boolean;
  updateNewCommunicationsCount: (delta: number) => void;
}

interface ProviderProps {
  children: React.ReactNode;
}

// define context
const CompanyContext: ICompanyContext | any = React.createContext({});

// define provider
export function CompanyProvider({ children }: ProviderProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [isValidated, setIsValidated] = useState(true);
  const [id, setId] = useState<number>(0);
  const [name, setName] = useState<string | null>(null);
  const [profileImageUrl, setProfileImageUrl] = useState<string>('');
  const [premium, setPremium] = useState(false);
  const [profileURLName, setProfileURLName] = useState('');
  const [rating, setRating] = useState(0);
  const [reviewsCount, setReviewsCount] = useState(0);
  const [capabilities, setCapabilities] = useState<CompanyCapability[]>([]);
  const [indicators, setIndicators] = useState<ICompanyMemberIndicators>();

  function updateState(data: CompanyContextData) {
    // Handle explicit validation status from API
    if (data?.isValidated === false) {
      setIsValidated(false);
      return;
    }

    if (data?.company) {
      setIsValidated(true);
      setId(data.company.id);
      setName(data.company.name);
      setPremium(data.company.premium);
      setProfileURLName(data.company.profileURLName);
      setProfileImageUrl(data.company.profileImageUrl);
      setRating(data.company.rating);
      setReviewsCount(data.company.reviewsCount);
      setCapabilities(data.capabilities ?? []);
    }
  }
  // Use our new hook instead of the inline implementation
  const { indicators: notificationData, fetchNotifications } =
    useCompanyIndicators();

  useEffect(() => {
    setIndicators(notificationData);
  }, [notificationData]);

  const { data: companyData, refetch: fetchCompany } = useQuery({
    queryKey: ['company'],
    queryFn: getCompany,
  });

  useEffect(() => {
    updateState(companyData);
    setIsLoading(false);
  }, [companyData]);

  // Update newCommunicationsCount in indicators by delta (for real-time counter updates)
  const updateNewCommunicationsCount = useCallback((delta: number) => {
    setIndicators((prev) => {
      if (!prev) return prev;

      clearCompanyIndicatorsCache();

      return {
        ...prev,
        newCommunicationsCount: Math.max(
          0,
          (prev.newCommunicationsCount ?? 0) + delta
        ),
      };
    });
  }, []);

  const contextValue: ICompanyContext = useMemo(() => {
    const hasCapability = (capabilityType: CapabilityType): boolean => {
      return (
        capabilities &&
        capabilities.find(
          (capability) => capability.capabilityType === capabilityType
        ) !== undefined
      );
    };

    return {
      isLoading,
      isValidated,
      id,
      name,
      profileImageUrl,
      premium,
      profileURLName,
      rating,
      reviewsCount,
      indicators,
      capabilities,
      fetchCompany,
      fetchNotifications,
      hasCapability,
      updateNewCommunicationsCount,
    };
  }, [
    isLoading,
    isValidated,
    id,
    name,
    profileImageUrl,
    premium,
    profileURLName,
    rating,
    reviewsCount,
    indicators,
    capabilities,
    fetchCompany,
    fetchNotifications,
    updateNewCommunicationsCount,
  ]);

  return (
    <CompanyContext.Provider value={contextValue}>
      {children}
    </CompanyContext.Provider>
  );
}

export function useCompanyContext() {
  const context: ICompanyContext = useContext(CompanyContext);
  if (context === undefined) {
    throw new Error(
      'useCompanyContext must be used within a CompanyProvider. Wrap a parent component in <CompanyProvider> to fix this error.'
    );
  }
  return context;
}
